import type {
  AccessRequestResponse,
  MeResponse,
  MembershipSummaryResponse,
  MyAccessRequestStatusResponse,
  Permission,
  PendingInvitationResponse,
  PendingPlatformInvitationResponse,
  SessionUserResponse,
  SubmitJoinAccessRequest,
  SubmitNewTenantAccessRequest,
  ValidatedIdentityResponse,
} from '@contract-analyzer/api-client';
import { createContext } from 'react';

import type { DevPersona } from '@/lib/session-auth';

type AuthUserIdentity = Pick<
  MeResponse,
  'created_at' | 'display_name' | 'email' | 'is_superadmin' | 'status' | 'user_audience' | 'user_id'
>;

// The session's deadline is not part of the user: activity moves it every minute or so, and
// every page keyed on the user would reload with it. The auth provider keeps it to itself.
type AuthUserSession = Pick<
  SessionUserResponse,
  'demo_mode' | 'membership_id' | 'tenant_display_name' | 'tenant_id' | 'tenant_slug'
> & {
  readonly permissions: SessionUserResponse['tenant_permissions'];
};

export interface AuthUser extends AuthUserIdentity, AuthUserSession {
  readonly accepted_terms_at: string | null;
}

export type OnboardingIdentity = ValidatedIdentityResponse;

export interface AuthState {
  readonly availableMemberships: readonly MembershipSummaryResponse[];
  readonly error: string | null;
  readonly isAuthenticated: boolean;
  readonly isLoading: boolean;
  readonly onboardingIdentity: OnboardingIdentity | null;
  readonly onboardingRequest: AccessRequestResponse | null;
  readonly pendingInvitations: readonly PendingInvitationResponse[];
  readonly pendingPlatformInvitations: readonly PendingPlatformInvitationResponse[];
  readonly requiresOnboarding: boolean;
  readonly termsAccepted: boolean;
  readonly user: AuthUser | null;
  readonly hasPermission: (permission: Permission) => boolean;
  readonly login: (devPersona?: DevPersona) => Promise<void>;
  readonly logout: () => Promise<void>;
  readonly acceptTerms: () => Promise<void>;
  readonly refreshOnboardingRequest: () => Promise<MyAccessRequestStatusResponse>;
  readonly retryOnboardingBootstrap: () => Promise<void>;
  readonly submitJoinTenantRequest: (
    input: SubmitJoinAccessRequest,
  ) => Promise<MyAccessRequestStatusResponse>;
  readonly submitNewTenantRequest: (
    input: SubmitNewTenantAccessRequest,
  ) => Promise<MyAccessRequestStatusResponse>;
  readonly switchTenant: (tenantId: string) => Promise<void>;
  /** Present the workspace through the superadmin's own membership, or end that. */
  readonly setDemoMode: (enabled: boolean) => Promise<void>;
}

export const AuthContext = createContext<AuthState | null>(null);
